import { useEffect, useRef, useState } from "react";
import Confetti from "../Confetti";
import vw from "../../api/virtualWorld.api";

const cr = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

function isGoneError(e) {
  const status = e?.status || e?.data?.status;
  const msg = String(e?.data?.message || e?.message || "");
  return status === 404 || /Keine ausstehende|Kein ausstehendes/i.test(msg);
}

/**
 * Zweistufig: 1) Zeremonie (Preisgeld/Konfetti) → 2) Saison-Übersicht → neue Saison.
 */
const SeasonEndOverlay = ({ userId, ceremony, review, onDone, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [localCeremony, setLocalCeremony] = useState(ceremony || null);
  const [localReview, setLocalReview] = useState(review || null);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (finishedRef.current) return;
    if (!ceremony) {
      setLocalCeremony(null);
      setLocalReview(null);
      return;
    }
    setLocalCeremony((prev) => {
      // Nicht von Review zurück auf „Meister!“ springen (stale Refresh)
      if (
        prev?.phase === "review" &&
        ceremony.phase !== "review" &&
        Number(prev.seasonNumber) === Number(ceremony.seasonNumber)
      ) {
        return { ...ceremony, phase: "review" };
      }
      return ceremony;
    });
    setLocalReview((prev) => review || prev || null);
  }, [ceremony, review]);

  if (!localCeremony) return null;

  const phase = localCeremony.phase === "review" || localReview ? "review" : "ceremony";
  const champion = Boolean(localCeremony.champion);
  const rec = localReview?.record || {
    wins: localCeremony.wins,
    draws: localCeremony.draws,
    losses: localCeremony.losses,
    points: localCeremony.points,
    scoredFor: localCeremony.scoredFor,
    scoredAgainst: localCeremony.scoredAgainst,
  };

  const dismiss = async () => {
    finishedRef.current = true;
    setLocalCeremony(null);
    setLocalReview(null);
    setErr("");
    if (onRefresh) await onRefresh();
    if (onDone) onDone();
  };

  const ackCeremony = async () => {
    if (!userId || busy || finishedRef.current) return;
    setBusy(true);
    setErr("");
    try {
      const res = await vw.acknowledgeSeasonCeremony(userId);
      setLocalCeremony(res.data?.seasonCeremony || { ...localCeremony, phase: "review" });
      setLocalReview(res.data?.seasonReview || null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      if (isGoneError(e)) {
        await dismiss();
        return;
      }
      setErr(e?.data?.message || e?.message || "Zeremonie konnte nicht bestätigt werden.");
    } finally {
      setBusy(false);
    }
  };

  const ackReview = async () => {
    if (!userId || busy || finishedRef.current) return;
    setBusy(true);
    setErr("");
    try {
      await vw.acknowledgeSeasonReview(userId);
      await dismiss();
    } catch (e) {
      if (isGoneError(e)) {
        await dismiss();
        return;
      }
      // Saison oft schon gestartet — Modal schließen statt hängen + Fehler-Loop
      const msg = e?.data?.message || e?.message || "";
      if (/Saison|bereits|Review/i.test(String(msg))) {
        await dismiss();
        return;
      }
      setErr(msg || "Saison-Übersicht konnte nicht abgeschlossen werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/75"
      role="dialog"
      aria-modal="true"
    >
      {phase === "ceremony" ? <Confetti /> : null}
      <div className="relative max-w-lg w-full rounded-3xl bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800 border border-amber-300/50 p-7 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="pointer-events-none absolute inset-0 opacity-30 text-3xl leading-none select-none">
          {champion ? "🏆✨🎊✨🏆✨🎊✨🏆" : "📋✨🏅✨📋✨🏅"}
        </div>
        <div className="relative space-y-4">
          {phase === "ceremony" ? (
            <>
              <p className="text-xs uppercase tracking-widest opacity-60">
                Saison {localCeremony.seasonNumber} · Abschluss
              </p>
              <h2 className="text-3xl font-black">
                {champion ? "Meister!" : `Platz ${localCeremony.place}`}
              </h2>
              <p className="opacity-80">
                {champion
                  ? `${localCeremony.clubName || "Dein Club"} schließt die Saison als Tabellenerster ab.`
                  : `${localCeremony.clubName || "Dein Club"} beendet die Saison auf Platz ${localCeremony.place}.`}
              </p>
              <ul className="space-y-2 text-sm">
                {localCeremony.prize ? (
                  <li className="flex justify-between gap-4">
                    <span>Preisgeld eingespielt</span>
                    <strong className="text-emerald-700 dark:text-emerald-300">{cr(localCeremony.prize)}</strong>
                  </li>
                ) : null}
                {localCeremony.sponsorBonus ? (
                  <li className="flex justify-between gap-4">
                    <span>{localCeremony.sponsorName || "Sponsor"}-Bonus</span>
                    <strong>{cr(localCeremony.sponsorBonus)}</strong>
                  </li>
                ) : null}
                {localCeremony.promoted ? (
                  <li className="text-emerald-700 dark:text-emerald-300 font-semibold">Aufstieg!</li>
                ) : null}
                <li className="opacity-70 text-xs pt-1">
                  Bilanz: {Number(rec.wins) || 0}S · {Number(rec.draws) || 0}U · {Number(rec.losses) || 0}N
                  {rec.points != null ? ` · ${rec.points} Pkt.` : ""}
                </li>
              </ul>
              <button
                type="button"
                disabled={busy}
                onClick={ackCeremony}
                className="w-full px-5 py-3 rounded-xl bg-amber-700 text-amber-50 font-bold disabled:opacity-50"
              >
                {busy ? "…" : "Weiter zur Saison-Übersicht"}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs uppercase tracking-widest opacity-60">
                Saison {localCeremony.seasonNumber} · Rückblick
              </p>
              <h2 className="text-2xl font-black">Highlights & Transfers</h2>
              <p className="text-sm opacity-75">
                Kurzer Blick auf die abgelaufene Saison — danach startet die neue Liga.
              </p>
              <div className="rounded-2xl bg-white/60 dark:bg-zinc-800/60 p-4 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Platz</span>
                  <strong>
                    {localCeremony.place}
                    {champion ? " · Meister" : ""}
                  </strong>
                </div>
                <div className="flex justify-between">
                  <span>Bilanz</span>
                  <strong>
                    {Number(rec.wins) || 0}/{Number(rec.draws) || 0}/{Number(rec.losses) || 0}
                  </strong>
                </div>
                <div className="flex justify-between">
                  <span>Punkte / Legs</span>
                  <strong>
                    {Number(rec.points) || 0} · {Number(rec.scoredFor) || 0}:
                    {Number(rec.scoredAgainst) || 0}
                  </strong>
                </div>
                <div className="flex justify-between">
                  <span>Preisgeld</span>
                  <strong>{cr(localCeremony.prize)}</strong>
                </div>
              </div>
              {localReview?.highlights?.length ? (
                <div>
                  <p className="text-xs uppercase tracking-widest opacity-50 mb-2">Top-Leistungen</p>
                  <ul className="space-y-1.5 text-sm">
                    {localReview.highlights.slice(0, 5).map((h, i) => (
                      <li
                        key={`${h.playerKey}-${i}`}
                        className="flex justify-between gap-3 rounded-lg bg-white/50 dark:bg-zinc-800/50 px-3 py-2"
                      >
                        <span className="truncate opacity-80">
                          {h.playerKey?.replace(/^generated:/, "") || "Spieler"}
                        </span>
                        <span className="shrink-0 opacity-70">
                          {h.highFinish ? `HF ${h.highFinish}` : ""}
                          {h.maximums ? ` · ${h.maximums}×180` : ""}
                          {h.virtualAverage ? ` · Ø ${Math.round(h.virtualAverage)}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm opacity-60">
                  Keine Highlight-Stats gespeichert — die nächste Saison zählt neu.
                </p>
              )}
              {localReview?.ledger?.length ? (
                <div>
                  <p className="text-xs uppercase tracking-widest opacity-50 mb-2">Kasse & Transfers</p>
                  <ul className="space-y-1 text-sm max-h-36 overflow-y-auto">
                    {localReview.ledger.map((f, i) => (
                      <li key={i} className="flex justify-between gap-3 opacity-80">
                        <span className="truncate">{f.note || f.type}</span>
                        <span className="shrink-0">{cr(f.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <button
                type="button"
                disabled={busy}
                onClick={ackReview}
                className="w-full px-5 py-3 rounded-xl bg-emerald-700 text-emerald-50 font-bold disabled:opacity-50"
              >
                {busy ? "…" : "Okay — neue Saison starten"}
              </button>
              {err ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => dismiss()}
                  className="w-full px-5 py-2.5 rounded-xl border border-amber-700/40 text-sm font-semibold opacity-90"
                >
                  Trotzdem schließen / neue Saison laden
                </button>
              ) : null}
            </>
          )}
          {err ? <p className="text-sm text-red-600">{err}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default SeasonEndOverlay;
